import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  Check,
  ChevronDown,
  CreditCard,
  Download,
  Tag,
  X,
} from "react-bootstrap-icons";
import { Form } from "react-bootstrap";
import { GraphQlSubscriptionPlan } from "../../graphql/schema/types";

import {
  usePaymentDataQuery,
  useTariffDataQuery,
  useUnsubscribeMutation,
  useSubscribeMutation,
} from "../../graphql/payment.hooks";
import moment from "moment/moment";
import { CustomToast } from "../../components/toast";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { CancelSubscriptionModal } from "../../components/modal/cancelSubscription";

export type PlanId = GraphQlSubscriptionPlan;

const PaymentSubscription = () => {
  const { t } = useTranslation();
  const lng = useGetUrlLocale();
  const [coupon, setCoupon] = useState<string>("");
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showPromo, setShowPromo] = useState<boolean>(false);
  const {
    data: paymentData,
    loading: paymentLoading,
    refetch: refetchPayment,
  } = usePaymentDataQuery();

  const { data: tariffData, refetch: refetchTariff } = useTariffDataQuery({
    variables: {},
  });

  const [cancelSubscription] = useUnsubscribeMutation();
  const [getSubscription] = useSubscribeMutation();
  const isPremium = paymentData?.user?.premium !== null;
  const order = paymentData?.user?.premium?.activeOrder ?? null;
  const prices = tariffData?.countryPrices;

  const [selectedPlan, setSelectedPlan] = useState<PlanId>(
    order?.isMonthly
      ? GraphQlSubscriptionPlan.PremiumMonthly
      : order?.isYearly
        ? GraphQlSubscriptionPlan.PremiumYearly
        : GraphQlSubscriptionPlan.PremiumYearly
  );
  const [isApplying, setIsApplying] = useState(false);

  const handleApplyCoupon = useCallback(
    async (value?: string) => {
      const finalCoupon = value ?? "";
      if (!finalCoupon.trim()) return;

      setIsApplying(true);
      setAppliedCoupon(finalCoupon);

      await refetchTariff({
        coupon: finalCoupon.trim(),
      });

      setIsApplying(false);
    },
    [refetchTariff]
  );
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const couponParam = params.get("coupon");

    if (couponParam) {
      setCoupon(couponParam);
      setShowPromo(true);

      handleApplyCoupon(couponParam);

      params.delete("coupon");

      const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : "");

      window.history.replaceState({}, "", newUrl);
    }
  }, [handleApplyCoupon]);

  const isCurrentPlan =
    (selectedPlan === GraphQlSubscriptionPlan.PremiumMonthly &&
      order?.isMonthly) ||
    (selectedPlan === GraphQlSubscriptionPlan.PremiumYearly && order?.isYearly);

  const handlePlanChange = (planId: PlanId) => {
    setSelectedPlan(planId);
  };
  const handleUnSubscribe = async () => {
    if (!selectedPlan || !order?.id) return;

    try {
      await cancelSubscription({
        variables: { orderId: order.id, planId: selectedPlan },
      });

      await refetchPayment();

      setShowToast(true);
    } catch (err) {
      console.error(err);
    }
  };
  const handleSubscribe = async () => {
    if (!selectedPlan) return;

    try {
      const { data } = await getSubscription({
        variables: {
          currencyCode: "usd",
          planId: selectedPlan,
          targetCancelLink:
            lng === "en"
              ? `${window.location.origin}/payment`
              : `${window.location.origin}${lng}/payment`,
          coupon: coupon || undefined,
        },
      });
      if (window.gtag) {
        window.gtag("event", "prem_admin_subscribe");
        if (selectedPlan === GraphQlSubscriptionPlan.PremiumMonthly) {
          window.gtag("event", "monthly_subscription_success");
        } else if (selectedPlan === GraphQlSubscriptionPlan.PremiumYearly) {
          window.gtag("event", "subscription_for_year_success");
        }
      }

      if (data?.premiumPaymentMiddleware) {
        window.location.href = data.premiumPaymentMiddleware;
      }
    } catch (err) {
      console.error(err);
    }
  };
  const hasPromo = prices?.plans.some((plan) =>
    plan.currencies.some((c) => c.promocodeDiscount !== null)
  );
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);

  return (
    <Container>
      <div className="d-flex justify-content-between">
        <h1 className="mb-5 fs-2 mt-5 mt-lg-0">{t("menu.payment")}</h1>
      </div>
      {paymentLoading ? (
        <div className="d-flex align-items-center justify-content-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("profile.loading")}...</span>
          </Spinner>
        </div>
      ) : (
        <Row className="g-4">
          <Col lg={5}>
            <div className="p-lg-4 p-3 rounded-4 bg-white">
              <p className="fs-5 fw-bold mb-4">{t("payment.current")}</p>
              {isPremium ? (
                <>
                  <div className="border rounded p-3">
                    <div className="d-flex justify-content-between">
                      <div className="d-flex align-items-center">
                        <p className="fw-bold mb-0 me-2 fs-6">
                          {t("payment.premium-tariff")}
                        </p>
                        {order && (
                          <span className="py-1 px-2 rounded fs-7 bg-additional-gray text-gray d-inline-block">
                            {order?.isMonthly && t("payment.monthly")}
                            {order?.isYearly && t("payment.annually")}
                          </span>
                        )}
                      </div>
                      {order && (
                        <p className="mb-0">
                          <span className="fs-5 fw-bold">${order?.amount}</span>{" "}
                          <span className="fw-normal text-gray">
                            /{order?.isMonthly && t("payment.month")}
                            {order?.isYearly && t("payment.year")}
                          </span>
                        </p>
                      )}
                    </div>
                    <div className="my-4">
                      <hr />
                    </div>
                    {order ? (
                      <div className="">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <p className="fw-bold mb-0 ">{t("payment.method")}</p>
                          {paymentData?.user.premium.customerPortalUrl && (
                            <a
                              href={paymentData?.user.premium.customerPortalUrl}
                              className="text-decoration-none text-booking d-inline-flex align-items-center"
                            >
                              <CreditCard className="me-2" />
                              <span>{t("payment.editCard")}</span>
                            </a>
                          )}
                        </div>

                        <p className="text-gray mb-0">
                          {String(order?.status) === "CANCEL" && (
                            <>{t("payment.subscription-canceled")} </>
                          )}
                          {String(order?.status) === "FAIL" && (
                            <>{t("payment.subscription-unpaid")} </>
                          )}
                          {String(order?.status) === "SUCCESS" &&
                            (order?.isSchedulingToCancellation &&
                            order?.isSuccessStatus ? (
                              <>
                                {t(
                                  "payment.subscription-scheduling-cancellation"
                                )}{" "}
                                <span className="text-dark">
                                  {moment(
                                    paymentData?.user.premium.dueDate
                                  ).format("MMMM D, YYYY")}
                                </span>{" "}
                                {t(
                                  "payment.subscription-scheduling-cancellation-suffix"
                                )}
                              </>
                            ) : (
                              <>
                                {t("payment.subscription-charged")}{" "}
                                <span className="text-dark">
                                  {moment(
                                    paymentData?.user.premium.dueDate
                                  ).format("MMMM D, YYYY")}
                                </span>
                              </>
                            ))}
                          {(String(order?.status) === "CANCEL" ||
                            String(order?.status) === "FAIL") && (
                            <span className="text-dark">
                              {moment(paymentData?.user.premium.dueDate).format(
                                "MMMM D, YYYY"
                              )}
                            </span>
                          )}
                        </p>
                        {String(order?.status) !== "CANCEL" && (
                          <Button
                            variant="link"
                            onClick={() => setShowModal(true)}
                            className="text-dark px-0"
                          >
                            {t("payment.cancelPlan")}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <p className="text-gray mb-0">
                        {t("payment.benefit-period")}{" "}
                        <span className="text-dark">
                          {moment(paymentData?.user.premium.dueDate).format(
                            "MMMM D, YYYY"
                          )}
                        </span>
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="border rounded p-3 d-flex justify-content-between">
                  <div className="d-flex align-items-center">
                    <p className="fw-bold mb-0 me-2 fs-6">
                      {t("payment.free")}
                    </p>
                    <span className="py-1 px-2 rounded fs-7 bg-additional-gray text-gray d-inline-block">
                      {t("payment.w-ads")}
                    </span>
                  </div>
                  <p className="mb-0">
                    <span className="fs-5 fw-bold">$0</span>{" "}
                    <span className="fw-normal text-gray">
                      / {t("payment.forever")}
                    </span>
                  </p>
                </div>
              )}
            </div>
          </Col>
          <Col lg={7}>
            <div className="p-lg-4 p-3 rounded-4 bg-white">
              <div className="d-flex justify-content-between">
                <p className="fs-5 fw-bold mb-4">{t("payment.premium")}</p>
                <span
                  className="mb-0 text-primary cursor-pointer"
                  onClick={() => setShowPromo((prev) => !prev)}
                >
                  <Tag className="me-2" />
                  {t("payment.Use Promocode")}
                  <ChevronDown
                    className={`ms-2 transition ${showPromo ? "rotate-180" : ""}`}
                  />
                </span>
              </div>
              {showPromo && (
                <div className="row mb-3">
                  <div className="col-10">
                    <Form.Control
                      className="fw-bold"
                      type="text"
                      id="promoCode"
                      value={coupon}
                      onChange={(e) => setCoupon(e.target.value)}
                    />
                    {appliedCoupon && !hasPromo && (
                      <div className="text-danger mt-1">
                        {t("payment.Invalid promo code")}
                      </div>
                    )}
                    {appliedCoupon && hasPromo && (
                      <div className="text-success mt-1">
                        {t("payment.Applied!")}
                      </div>
                    )}
                  </div>
                  <div className="col-2 ps-0">
                    {!hasPromo ? (
                      <Button
                        className="w-100 px-0"
                        onClick={() => handleApplyCoupon(coupon)}
                        disabled={isApplying}
                      >
                        {isApplying
                          ? t("payment.Applying...")
                          : t("payment.Apply")}
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        className="w-100 text-white"
                        onClick={() => {
                          refetchTariff({ coupon: undefined } as {
                            coupon?: string;
                          });
                          setCoupon("");
                          setAppliedCoupon(null);
                        }}
                      >
                        {t("payment.Edit")}
                      </Button>
                    )}
                  </div>
                </div>
              )}
              <div className="border rounded p-lg-4 p-3">
                <Form>
                  <div className="mb-4 row gx-3">
                    <div className="col-lg-6 mb-3 mb-lg-0">
                      <div
                        className="border rounded p-3 d-flex justify-content-between align-items-center cursor-pointer"
                        onClick={() =>
                          handlePlanChange(
                            GraphQlSubscriptionPlan.PremiumYearly
                          )
                        }
                        role="button"
                      >
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="radio"
                            id="radio-yearly"
                            name="radioGroup"
                            className="me-2"
                            checked={
                              selectedPlan ===
                              GraphQlSubscriptionPlan.PremiumYearly
                            }
                            onClick={(e) => e.stopPropagation()}
                            onChange={() =>
                              handlePlanChange(
                                GraphQlSubscriptionPlan.PremiumYearly
                              )
                            }
                          />
                          <div className="d-xxl-flex">
                            <p className="fs-6 fw-bold mb-lg-0 mb-2 me-lg-2">
                              {t("payment.annually")}
                            </p>
                            <span className="py-1 px-2 fs-7 rounded bg-booking-primary text-booking d-inline-block text-nowrap">
                              <small>
                                {t("payment.save")} $
                                {(
                                  (prices?.plans[0]?.currencies.find(
                                    (c) => c.currencyCode === "usd"
                                  )?.amount ?? 0) *
                                    12 -
                                  (prices?.plans[0]?.currencies.find(
                                    (c) => c.currencyCode === "usd"
                                  )?.promocodeDiscount ?? 0) -
                                  (prices?.plans[1]?.currencies.find(
                                    (c) => c.currencyCode === "usd"
                                  )?.finalAmount ?? 0)
                                ).toFixed(0)}
                              </small>
                            </span>
                          </div>
                        </div>
                        <p className="mb-0 text-nowrap">
                          <span className="fs-5 fw-bold">
                            $
                            {
                              prices?.plans[1]?.currencies.find(
                                (c) => c.currencyCode === "usd"
                              )?.finalAmount
                            }
                          </span>{" "}
                          <span className="fw-normal text-gray">
                            / {t("payment.year")}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="col-lg-6">
                      <div
                        className="border rounded p-3 d-flex justify-content-between align-items-center h-100 cursor-pointer"
                        onClick={() =>
                          handlePlanChange(
                            GraphQlSubscriptionPlan.PremiumMonthly
                          )
                        }
                        role="button"
                      >
                        <div className="d-flex align-items-center">
                          <Form.Check
                            type="radio"
                            id="radio-monthly"
                            name="radioGroup"
                            className="me-2"
                            checked={
                              selectedPlan ===
                              GraphQlSubscriptionPlan.PremiumMonthly
                            }
                            onClick={(e) => e.stopPropagation()}
                            onChange={() =>
                              handlePlanChange(
                                GraphQlSubscriptionPlan.PremiumMonthly
                              )
                            }
                          />
                          <p className="fs-6 fw-bold mb-0">
                            {t("payment.monthly")}
                          </p>
                        </div>
                        <p className="mb-0">
                          <span className="fs-5 fw-bold">
                            $
                            {
                              prices?.plans[0]?.currencies.find(
                                (currency: { currencyCode: string }) =>
                                  currency.currencyCode === "usd"
                              )?.finalAmount
                            }
                          </span>{" "}
                          <span className="fw-normal text-gray">
                            / {t("payment.month")}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </Form>

                <div className="fs-7 mb-4">
                  <div className="d-flex p-2 border-bottom justify-content-between align-items-center">
                    <p className="mb-0">{t("payment.priority")}</p>
                    <p className="mb-0 fw-bold">
                      {"<"} {t("payment.minutes")}
                    </p>
                  </div>
                  <div className="d-flex p-2 border-bottom justify-content-between align-items-center">
                    <p className="mb-0">
                      <span className="d-lg-inline-block d-none">
                        {t("payment.feature-1")}
                      </span>
                      <span className="d-lg-none">
                        {t("payment.feature-1m")}
                      </span>
                    </p>
                    <Check className="text-success" />
                  </div>
                  <div className="d-flex p-2 border-bottom justify-content-between align-items-center">
                    <p className="mb-0">{t("payment.feature-2")}</p>
                    <Check className="text-success" />
                  </div>
                  <div className="d-flex p-2 border-bottom justify-content-between align-items-center">
                    <p className="mb-0">{t("payment.feature-3")}</p>
                    <Check className="text-success" />
                  </div>
                  <div className="d-flex p-2 justify-content-between align-items-center">
                    <p className="mb-0">{t("payment.feature-4")}</p>
                    <Check className="text-success" />
                  </div>
                </div>
                <button
                  className={`btn btn-primary text-white w-100 py-2 ${
                    isCurrentPlan && String(order?.status) !== "CANCEL"
                      ? "bg-light text-secondary border-light"
                      : "fw-bold"
                  }`}
                  disabled={isCurrentPlan && String(order?.status) !== "CANCEL"}
                  onClick={handleSubscribe}
                >
                  {isCurrentPlan && String(order?.status) !== "CANCEL"
                    ? t("payment.current-plan")
                    : t("payment.subscribe")}
                </button>
              </div>
            </div>
            {paymentData?.user.paymentHistory.length > 0 && (
              <div className="p-lg-4 p-3 rounded-4 bg-white mt-4">
                <p className="fs-5 fw-bold mb-4">{t("payment.payment")}</p>
                <table className="table table-borderless">
                  <thead>
                    <tr>
                      <th className="bg-soft-light-gray">
                        {t("payment.amount")}
                      </th>
                      <th className="bg-soft-light-gray">
                        {t("payment.subscription")}
                      </th>
                      <th className="bg-soft-light-gray">
                        {t("payment.date")}
                      </th>
                      <th className="bg-soft-light-gray">
                        {t("payment.status")}
                      </th>
                      <th className="bg-soft-light-gray">
                        {t("payment.invoice")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentData?.user.paymentHistory.map((history) => (
                      <tr key={history.id}>
                        <td className="py-3">
                          $<b>{history.finalAmount}</b>
                        </td>
                        <td className="py-3">{history.orderName}</td>
                        <td className="py-3">
                          {moment(history.createdAt).format("DD.MM.YYYY")}
                        </td>
                        <td className="py-3">
                          {(history.firstStatus
                            ? history.firstStatus === "SUCCESS" ||
                              history.firstStatus === "CANCEL"
                            : history.status === "SUCCESS" ||
                              history.status === "CANCEL") && (
                            <span className="px-2 py-2 rounded text-success bg-success-light fs-7">
                              <Check className="fs-5" />
                              {t("payment.completed")}
                            </span>
                          )}

                          {(history.firstStatus
                            ? history.firstStatus === "FAIL"
                            : history.status === "FAIL") && (
                            <span className="px-2 py-2 rounded text-primary bg-primary-light fs-7">
                              <X className="fs-5" />
                              {t("payment.failed")}
                            </span>
                          )}
                        </td>
                        <td className="py-3">
                          <a
                            className="btn btn-dark border-0 bg-white text-dark py-0 px-1 me-2"
                            href={history.pdfInvoiceUrl}
                          >
                            <Download />
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Col>
        </Row>
      )}
      <CustomToast
        isShow={showToast}
        setShow={setShowToast}
        message={t("payment.canceled-successfully")}
      />
      <CancelSubscriptionModal
        onCancel={() => setShowModal(false)}
        showModal={showModal}
        unSubscribe={handleUnSubscribe}
      />
    </Container>
  );
};

export default PaymentSubscription;
